import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, money, slotLabel, formatApiErrorDetail } from "@/api";
import { useAuth } from "@/AuthContext";
import { toast } from "sonner";
import { Star, MapPin, Award, Video, Loader2, ShieldCheck, Users } from "lucide-react";

export default function CoachProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [coach, setCoach] = useState(null);
  const [slots, setSlots] = useState([]);
  const [service, setService] = useState(null);
  const [slot, setSlot] = useState("");
  const [notes, setNotes] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [athlete, setAthlete] = useState("");
  const [busy, setBusy] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [fees, setFees] = useState({ client_fee_percent: 2.5, coach_fee_percent: 2.5 });

  useEffect(() => { api.get("/pricing/fees").then((r) => setFees(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get(`/coaches/${id}/reviews`).then((r) => setReviews(r.data)).catch(() => {}); }, [id]);

  useEffect(() => {
    api.get(`/coaches/${id}`).then((r) => { setCoach(r.data); setService(r.data.services[0]); }).catch(() => nav("/coaches"));
    api.get(`/coaches/${id}/availability`).then((r) => setSlots(r.data)).catch(() => {});
  }, [id, nav]);

  const needsSlot = service?.kind === "ice" || service?.kind === "group";

  const book = async () => {
    if (!user) { toast.error("Log in to book a session"); nav("/login"); return; }
    if (needsSlot && !slot) { toast.error("Pick a time slot first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/bookings/checkout", {
        coach_id: id, lookup_key: service.lookup_key, slot: needsSlot ? slot : null,
        notes, athlete_name: athlete, video_url: videoUrl, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  if (!coach) return <div className="p-20 text-center text-zinc-500">Loading coach…</div>;

  const clientFee = Math.floor((service?.price || 0) * 100 * fees.client_fee_percent / 100 + 0.5) / 100;
  const total = (service?.price || 0) + clientFee;

  return (
    <div data-testid="coach-profile-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7 space-y-12">
          <div className="relative border border-white/10 overflow-hidden">
            <img src={coach.photo} alt={coach.name} className="w-full h-64 sm:h-80 object-cover opacity-60" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <span className="inline-block bg-[#00e5ff] text-black text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 mb-3">{coach.category || "Goalie Coach"}</span>
              <h1 data-testid="coach-name" className="display text-4xl sm:text-5xl uppercase tracking-tighter font-bold leading-none flex items-center gap-3">
                {coach.name}
                {coach.verified && (
                  <span data-testid="coach-verified-badge" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-[#00e5ff] text-black px-2 py-1">
                    <ShieldCheck className="w-3 h-3" /> Verified
                  </span>
                )}
              </h1>
              <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-zinc-300">
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3 text-[#00e5ff]" /> {coach.city}, {coach.region}</span>
                <span className="flex items-center gap-1"><Star className="w-3 h-3 text-[#00e5ff] fill-[#00e5ff]" /> {coach.rating} ({coach.reviews_count} {coach.reviews_count === 1 ? "review" : "reviews"})</span>
                <span>{coach.experience_years} years coaching</span>
              </div>
            </div>
          </div>

          <div>
            <h2 className="display text-2xl uppercase tracking-tight font-bold">About</h2>
            <p className="text-sm text-zinc-300 leading-relaxed mt-4">{coach.bio || coach.headline}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            <div>
              <h3 className="text-[11px] uppercase tracking-[0.25em] text-zinc-500">Specialties</h3>
              <div className="flex flex-wrap gap-2 mt-3">
                {coach.specialties.map((s) => <span key={s} className="text-[11px] uppercase border border-[#00e5ff]/40 text-[#00e5ff] px-3 py-1.5">{s}</span>)}
              </div>
            </div>
            <div>
              <h3 className="text-[11px] uppercase tracking-[0.25em] text-zinc-500">Levels coached</h3>
              <div className="flex flex-wrap gap-2 mt-3">
                {coach.levels.map((s) => <span key={s} className="text-[11px] uppercase border border-white/15 text-zinc-300 px-3 py-1.5">{s}</span>)}
              </div>
            </div>
          </div>

          {coach.certifications?.length > 0 && (
            <div>
              <h3 className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3">Certifications</h3>
              <ul className="space-y-2">
                {coach.certifications.map((c) => (
                  <li key={c} className="flex items-center gap-2 text-sm text-zinc-300"><Award className="w-4 h-4 text-[#ff3b30]" /> {c}</li>
                ))}
              </ul>
            </div>
          )}

          <div data-testid="reviews-section">
            <h2 className="display text-2xl uppercase tracking-tight font-bold">Reviews</h2>
            {reviews.length === 0 ? (
              <p data-testid="no-reviews" className="text-sm text-zinc-500 mt-4 border border-white/10 p-6">No reviews yet — be the first after your session.</p>
            ) : (
              <div className="mt-5 space-y-3">
                {reviews.map((r) => (
                  <div key={r.id} data-testid={`review-${r.id}`} className="border border-white/10 bg-[#121212] p-5">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold">{r.user_name}</p>
                      <div className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? "text-[#00e5ff] fill-[#00e5ff]" : "text-zinc-700"}`} />
                        ))}
                      </div>
                    </div>
                    <p className="text-[11px] text-zinc-500 mt-1">{r.service_title} · {new Date(r.created_at).toLocaleDateString()}</p>
                    {r.comment && <p className="text-sm text-zinc-300 mt-3 leading-relaxed">{r.comment}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-5 lg:sticky lg:top-24 self-start">
          <div className="bg-[#121212] border border-white/10 p-6 lg:p-8 space-y-6">
            <div>
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">Book with {coach.name.split(" ")[0]}</span>
              <h2 className="display text-2xl uppercase tracking-tight font-bold mt-1">Choose a service</h2>
            </div>

            <div className="space-y-2">
              {coach.services.map((s) => (
                <button
                  key={s.lookup_key}
                  data-testid={`service-${s.lookup_key}`}
                  onClick={() => { setService(s); setSlot(""); }}
                  className={`w-full text-left border px-4 py-3 flex items-center justify-between transition-colors duration-200 ${
                    service?.lookup_key === s.lookup_key ? "border-[#00e5ff] bg-[#00e5ff]/5" : "border-white/10 hover:border-white/30"
                  }`}
                >
                  <span>
                    <span className="text-sm font-semibold block">{s.title}</span>
                    <span className="text-[11px] text-zinc-500 flex items-center gap-1">
                      {s.kind === "video" ? <><Video className="w-3 h-3" /> Remote · 48h turnaround</>
                        : s.kind === "remote" ? <><Video className="w-3 h-3" /> Remote program · 48h</>
                        : s.kind === "group" ? <><Users className="w-3 h-3" /> {s.duration_min} min · up to {s.capacity} goalies · price per goalie</>
                        : `${s.duration_min} minute session`}
                    </span>
                  </span>
                  <span className="display text-xl font-bold text-[#00e5ff]">{money(s.price)}</span>
                </button>
              ))}
            </div>

            {needsSlot ? (
              <div>
                <span className="text-[10px] uppercase tracking-widest text-zinc-500">
                  {service.kind === "group" ? "Group session times" : "Available slots"}
                </span>
                <div data-testid="availability-grid" className="mt-3 grid grid-cols-2 gap-2 max-h-56 overflow-y-auto pr-1">
                  {slots.length === 0 && <p className="text-xs text-zinc-500 col-span-2">This coach has not published availability yet.</p>}
                  {slots.map((s) => {
                    const spots = service.kind === "group" ? (s.group_spots?.[service.lookup_key] ?? service.capacity) : null;
                    const disabled = service.kind === "group" ? (s.booked || spots === 0) : s.booked;
                    return (
                      <button
                        key={s.slot}
                        data-testid={`slot-${s.slot}`}
                        disabled={disabled}
                        onClick={() => setSlot(s.slot)}
                        className={`text-[11px] px-2 py-2 border transition-colors duration-200 ${
                          disabled ? "border-white/5 text-zinc-600 line-through cursor-not-allowed"
                            : slot === s.slot ? "border-[#00e5ff] bg-[#00e5ff] text-black font-bold"
                            : "border-white/10 text-zinc-300 hover:border-[#00e5ff]"
                        }`}
                      >
                        {slotLabel(s.slot)}
                        {service.kind === "group" && !s.booked && (
                          <span className="block text-[9px] opacity-70">{spots} spot{spots === 1 ? "" : "s"} left</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : service?.kind === "video" ? (
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-zinc-500">Game film link (YouTube, Drive, etc.)</span>
                <input data-testid="video-url-input" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)}
                  placeholder="https://…"
                  className="mt-2 w-full bg-black border border-white/10 text-sm px-3 py-2.5 focus:border-[#00e5ff] focus:outline-none focus:ring-1 focus:ring-[#00e5ff] transition-colors duration-200" />
              </label>
            ) : (
              <p className="text-xs text-zinc-500 border border-white/10 p-4">Remote program — your trainer will send a personalised plan and check-ins after checkout.</p>
            )}

            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">Goalie's name (for the progress log)</span>
              <input data-testid="athlete-name-input" value={athlete} onChange={(e) => setAthlete(e.target.value)}
                placeholder="Who is this session for?"
                className="mt-2 w-full bg-black border border-white/10 text-sm px-3 py-2.5 focus:border-[#00e5ff] focus:outline-none focus:ring-1 focus:ring-[#00e5ff] transition-colors duration-200" />
            </label>

            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">Notes for the coach</span>
              <textarea data-testid="booking-notes-input" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="Age, level, what you want to work on…"
                className="mt-2 w-full bg-black border border-white/10 text-sm px-3 py-2.5 focus:border-[#00e5ff] focus:outline-none focus:ring-1 focus:ring-[#00e5ff] transition-colors duration-200" />
            </label>

            <div className="border-t border-white/10 pt-5 space-y-2">
              <div className="flex items-center justify-between text-xs text-zinc-400">
                <span>Session price</span>
                <span data-testid="booking-subtotal">{money(service?.price)}</span>
              </div>
              <div className="flex items-center justify-between text-xs text-zinc-400">
                <span>Service fee ({fees.client_fee_percent}%)</span>
                <span data-testid="booking-service-fee">${clientFee.toFixed(2)}</span>
              </div>
              <div className="flex items-end justify-between pt-3">
                <div>
                  <span className="text-[10px] uppercase text-zinc-500 block">Total today</span>
                  <span data-testid="booking-total" className="display text-3xl font-bold text-[#00e5ff] leading-none">${total.toFixed(2)}</span>
                  <span className="text-[10px] text-zinc-600 block mt-1">+ tax calculated at checkout</span>
                </div>
                <button
                  data-testid="book-and-pay-btn"
                  onClick={book}
                  disabled={busy}
                  className="bg-[#00e5ff] text-black font-bold uppercase tracking-wide px-7 py-4 flex items-center gap-2 hover:bg-[#00b3cc] disabled:opacity-50 transition-colors duration-200"
                >
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />} Book & pay
                </button>
              </div>
            </div>
            <p className="text-[10px] text-zinc-600">Secure card payment via Stripe. Free cancellation up to {fees.full_refund_hours ?? 24}h before the session; {fees.late_refund_percent ?? 50}% back after that.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
